import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Fingerprint } from "lucide-react";
import { NeuButton } from "../../components/neu/Neu";
import { SignatureCanvas } from "../pay/SignatureCanvas";
import { useApiError } from "../../hooks/useApiError";
import { BillVoucherSlip } from "./BillVoucherSlip";
import client from "../../api/client";
import { localName } from "../../lib/localName";

// نافذة توقيع المستلم — إيصال قبض رسمي + توقيع بالإصبع → ترحيل القيد وخصم الصندوق.
export const BillSignDialog = ({ bill, cashboxes = [], onDone, onCancel }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const cashboxLabel = useMemo(() => {
    if (!bill.cashbox_id) return t("acct.methods.credit");
    const box = cashboxes.find((b) => b.id === bill.cashbox_id);
    if (!box) return "—";
    const icon = box.kind === "bank" ? "🏦" : "💰";
    return `${icon} ${localName(box)}`;
  }, [bill.cashbox_id, cashboxes, t]);

  const submit = async () => {
    if (!sig) { toast.error(t("acct.bills.sign.needSig")); return; }
    if (!bill.receiver_name) { toast.error(t("acct.bills.sign.needName")); return; }
    setBusy(true);
    try {
      await client.post(`/api/accounting/bills/${bill.id}/sign`,
                        { signature_dataurl: sig, receiver_name: bill.receiver_name });
      toast.success(t("acct.bills.sign.done"));
      onDone();
    } catch (error) { toast.error(describe(error)); } finally { setBusy(false); }
  };

  const canSubmit = Boolean(sig) && Boolean(bill.receiver_name);

  return (
    <div className="fixed inset-0 z-50 grid place-content-center bg-black/60 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} data-testid="bill-sign-dialog"
           className="w-[min(640px,95vw)] max-h-[92vh] overflow-y-auto rounded-3xl border border-primary/30 bg-background p-6 shadow-2xl">
        <div className="flex items-center gap-2 text-primary">
          <Fingerprint className="h-6 w-6" />
          <h3 className="text-lg font-black">{t("acct.bills.sign.title")}</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("acct.bills.sign.hint")}</p>

        <div className="mt-4">
          <BillVoucherSlip bill={bill} cashboxLabel={cashboxLabel} />
        </div>

        <div className="mt-4">
          <p className="label-xs mb-2 flex items-center gap-2 text-primary">
            <Fingerprint className="h-4 w-4" /> {t("acct.bills.sign.canvas")} *
          </p>
          <SignatureCanvas onChange={setSig} />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <NeuButton data-testid="sign-cancel" onClick={onCancel}>{t("app.cancel")}</NeuButton>
          <NeuButton variant="primary" data-testid="sign-confirm" disabled={busy || !canSubmit} onClick={submit}>
            <Fingerprint className="h-4 w-4" /> {t("acct.bills.sign.confirm")}
          </NeuButton>
        </div>
      </div>
    </div>
  );
};
